import React, { useEffect, useState, useCallback } from "react";
import FaiBox from "../../../../MuiComponents/FaiBox";
import DataTable from "../../../../components/DataTable";
import {
	ProductController,
	ApiResponse,
} from "../../../../controllers/productController";
import {
	ProductListItem,
	ProductsResponse,
} from "../../../../types/productTypes";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Snackbar,
	Alert,
	Switch,
	alpha,
	Chip,
	Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { SwitchProps } from "@mui/material/Switch/Switch";
import EditProductModal from "./EditProductModal";
import EditIcon from "../../../../assets/images/icons/edit.svg";
import DeleteIcon from "@mui/icons-material/Delete";
import FaiButton from "../../../../MuiComponents/FaiButton";
import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import EditDraftModal from "../../Draft/EditDraftModal";

interface ProductListProps {
	data: any;
	filters?: any;
	[key: string]: any;
}

// Custom styled switch for consistent styling
const CustomSwitch = styled(Switch)<SwitchProps>(({ theme }) => ({
	"& .MuiSwitch-switchBase.Mui-checked": {
		color: theme.palette.success.main,
		"&:hover": {
			backgroundColor: alpha(
				theme.palette.success.main,
				theme.palette.action.hoverOpacity
			),
		},
	},
	"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
		backgroundColor: theme.palette.success.main + " !important",
		borderColor: theme.palette.success.main + " !important",
	},
}));

function ProductList(props: ProductListProps) {
	const { filters = {} } = props;
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [productData, setProductData] = useState<any>(props.data);
	const [totalItems, setTotalItems] = useState<number>(0);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
	const [selectedProduct, setSelectedProduct] = useState<any>(
		null
	);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
	const [productToDelete, setProductToDelete] = useState<string | null>(null);
	const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
	const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
		"success"
	);
	const [toggleStatusLoading, setToggleStatusLoading] = useState<{
		[key: string]: boolean;
	}>({});

	const fetchProductList = useCallback(
		async (
			page: number = 0,
			size: number = 10,
			currentFilters: any = {},
			retryCount = 0
		) => {
			try {
				setLoading(true);

				const queryParams = {
					page,
					size,
					...currentFilters,
				};

				const response = await ProductController.getMerchantProducts(
					queryParams
				);

				if (response?.data?.status === 200 && response?.data?.data) {
					const apiResponse = response.data as ApiResponse<ProductsResponse>;
					const productListData = apiResponse.data;

					const formattedData = formatProductData(productListData.data);

					const updatedColumns = props.data.columns.map((col: any) => {
						if (col.accessor === "edit") {
							return {
								...col,
								Cell: ({ row }: any) => (
									<Stack direction="row" spacing={2}>
										<FaiBox
											component="img"
											src={EditIcon}
											height="20px"
											sx={{ cursor: "pointer" }}
											onClick={() => {
												handleEditClick(row.original);
											}}
										/>
										{/* <MoveToDraft
											row={row.original}
											fetchInventoryProducts={fetchProductList}
										/> */}
										<DeleteIcon
											sx={{
												cursor: "pointer",
												color: "error.main",
												height: "20px",
												width: "auto",
											}}
											onClick={() => {
												handleDeleteClick(row.original.id);
											}}
										/>
									</Stack>
								),
							};
						} else if (col.accessor === "published") {
							return {
								...col,
								Cell: ({ value, row }: any) => (
									<CustomSwitch
										checked={value}
										onChange={() =>
											handleTogglePublished(row.original.id, !value)
										}
										disabled={toggleStatusLoading[row.original.id]}
									/>
								),
							};
						}
						return col;
					});

					setProductData({
						columns: updatedColumns,
						rows: formattedData,
					});

					setTotalItems(productListData.totalElements);
					setTotalPages(productListData.totalPages);
					setCurrentPage(page);

					setError(null);
				} else {
					setSnackbarMessage(
						"Received an invalid response format from the server."
					);
					setSnackbarSeverity("error");
					setSnackbarOpen(true);
				}
			} catch (error: any) {
				if (error.message === "Network Error" && retryCount < 3) {
					setTimeout(() => {
						fetchProductList(page, size, currentFilters, retryCount + 1);
					}, 2000);
					return;
				}

				setSnackbarMessage(
					`Failed to load products: ${
						error.message || "Unknown error"
					}. Please try again later.`
				);
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
			} finally {
				setLoading(false);
			}
		},
		[props.data, toggleStatusLoading]
	);

	useEffect(() => {
		setCurrentPage(0);
		fetchProductList(0, rowsPerPage, filters);
	}, [filters]);

	useEffect(() => {
		fetchProductList(currentPage, rowsPerPage, filters);
	}, [currentPage, rowsPerPage, fetchProductList]);

	const handleEditClick = async (product: any) => {
		try {
			// Verify product exists before opening modal
			const response = await ProductController.getProductToUpdate(product?.id);
			if (response?.data?.status === 200 && response?.data?.data) {
				setSelectedProduct(product);
				setEditModalOpen(true);
			} else {
				setSnackbarMessage(
					"Unable to edit this product. Product data could not be retrieved."
				);
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
			}
		} catch (error: any) {
			setSnackbarMessage(
				`Unable to edit product: ${error.message || "Unknown error"}`
			);
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		}
	};

	const handleTogglePublished = async (
		productId: string,
		isPublished: boolean
	) => {
		setToggleStatusLoading((prev) => ({ ...prev, [productId]: true }));
		try {
			const response = await ProductController.updateProductStatus(
				productId,
				isPublished
			);
			if (response?.data?.status) {
				setSnackbarMessage(
					`Product ${isPublished ? "published" : "unpublished"} successfully`
				);
				setSnackbarSeverity("success");
				setSnackbarOpen(true);

				// Update the row data directly without refetching
				setProductData((prevData) => ({
					...prevData,
					rows: prevData.rows.map((row: any) =>
						row.id === productId ? { ...row, published: isPublished } : row
					),
				}));
			} else {
				setSnackbarMessage(
					"Failed to update product status. Please try again."
				);
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
			}
		} catch (error: any) {
			setSnackbarMessage(
				`Failed to update status: ${error.message || "Unknown error"}`
			);
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setToggleStatusLoading((prev) => ({ ...prev, [productId]: false }));
		}
	};

	const handleDeleteClick = (productId: string) => {
		setProductToDelete(productId);
		setDeleteConfirmOpen(true);
	};

	const handleCloseDeleteConfirm = () => {
		setDeleteConfirmOpen(false);
		setProductToDelete(null);
	};

	const handleConfirmDelete = async () => {
		if (!productToDelete) return;

		setDeleteLoading(true);
		try {
			const response = await ProductController.deleteProduct(productToDelete);
			if (response?.data?.status === 200) {
				setSnackbarMessage("Product deleted successfully");
				setSnackbarSeverity("success");
				setSnackbarOpen(true);
				fetchProductList(currentPage, rowsPerPage, filters);
			} else {
				setSnackbarMessage("Failed to delete product. Please try again later.");
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
			}
		} catch (error: any) {
			setSnackbarMessage(
				`Failed to delete product: ${
					error.message || "Unknown error"
				}. Please try again later.`
			);
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setDeleteLoading(false);
			setDeleteConfirmOpen(false);
			setProductToDelete(null);
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	const handleCloseEditModal = () => {
		setEditModalOpen(false);
		setSelectedProduct(null);
	};

	const handleProductUpdated = () => {
		fetchProductList(currentPage, rowsPerPage, filters);
	};

	const handleEditError = (errorMessage: string) => {
		setSnackbarMessage(errorMessage);
		setSnackbarSeverity("error");
		setSnackbarOpen(true);
	};

	const formatProductData = (data: ProductListItem[]) => {
		return data.map((product: ProductListItem) => {
			// dataTableData.tsx renders the status badge by checking
			// value === "in stock". Anything else falls back to out-of-stock.
			// totalInventoryQuantity comes from the backend's slim
			// ProductResponseDTO populated in getMerchantProducts.
			const inStock = (product.totalInventoryQuantity ?? 0) > 0;
			return {
				id: product.id,
				articleName: [product.productName],
				category: product.categoryName,
				brandName: product.brand,
				skuId: product.id.substring(0, 8),
				published: product.status === "PUBLISHED",
				status: inStock ? "in stock" : "out of stock",
				edit: "edit",
			};
		});
	};

	const handleTableChange = (pageIndex: number, pageSize: number) => {
		if (pageSize !== rowsPerPage) {
			setRowsPerPage(pageSize);
			setCurrentPage(0);
		} else if (pageIndex !== currentPage) {
			setCurrentPage(pageIndex);
		}
	};

	if (loading && (!productData.rows || productData.rows.length === 0)) {
		return (
			<FaiBox
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="300px"
			>
				<CircularProgress />
			</FaiBox>
		);
	}

	if (error && (!productData.rows || productData.rows.length === 0)) {
		return (
			<FaiBox
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="300px"
			>
				<div>{error}</div>
			</FaiBox>
		);
	}

	return (
		<FaiBox>
			<Box className="overflow-auto">
				<DataTable
					table={productData}
					pagination={{
						variant: "contained",
						color: "primary",
						first: currentPage === 0,
						hasNext: currentPage < totalPages - 1,
						hasPrevious: currentPage > 0,
						last: currentPage === totalPages - 1,
						numberOfElements: productData.rows.length,
						totalElements: totalItems,
						totalPages: totalPages,
						page: currentPage,
						pageSize: rowsPerPage,
					}}
					entriesPerPage={{
						defaultValue: rowsPerPage,
						entries: [5, 10, 25, 50],
					}}
					showTotalEntries={true}
					canSearch={true}
					isSorted={false}
					onPageChange={handleTableChange}
				/>
			</Box>

			<EditDraftModal
				open={editModalOpen}
				onClose={handleCloseEditModal}
				product={selectedProduct}
				onProductUpdated={handleProductUpdated}
				onError={handleEditError}
			/>

			<Dialog
				open={deleteConfirmOpen}
				onClose={handleCloseDeleteConfirm}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Are you sure you want to delete this product? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<FaiButton onClick={handleCloseDeleteConfirm} color="primary">
						Cancel
					</FaiButton>
					<FaiButton
						onClick={handleConfirmDelete}
						color="error"
						variant="contained"
						disabled={deleteLoading}
					>
						{deleteLoading ? "Deleting..." : "Delete"}
					</FaiButton>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbarSeverity}
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</FaiBox>
	);
}

const MoveToDraft = ({ row, fetchInventoryProducts }) => {
	const [draftLoading, setDraftLoading] = useState<any>(false);

	const moveProductToDraft = async (pId, pData = {}) => {
		try {
			if (draftLoading) return;
			setDraftLoading(pId);
			const { data } = await ProductController.updateProduct(pId, {
				isDraft: true,
				// ...pData,
			});
			toast.success(data.message);
			fetchInventoryProducts();
		} catch (error) {
			console.error(error);
		} finally {
			setDraftLoading(false);
		}
	};

	return (
		<FaiBox display="flex" gap={1} mb={1}>
			<Chip
				className={`!rounded-lg cursor-pointer ${
					draftLoading === row.id ? "disabled" : ""
				}`}
				label={draftLoading === row.id ? "Loading..." : "Move to Draft"}
				size="medium"
				color="secondary"
				onClick={() => {
					// console.log("product", product);
					moveProductToDraft(row.id, {
						categoryId: row.categoryId,
						productTypeId: row.productTypeId,
					});
				}}
			/>
		</FaiBox>
	);
};

export default ProductList;
